import { HttpErrorResponse } from "@angular/common/http";
import { SanitizerWrapperService } from "../services";

type BatchRowFallback = (rowIndex: number) => string;
type BatchFieldFallback = (rowIndex: number, field: string) => string;

const batchValidationField = (key: string): { rowIndex: number; field: string } | null => {
    const match = /^FileMovementRequests\[(\d+)]\.(.+)$/.exec(key);
    if (!match) return null;

    return {
        rowIndex: Number(match[1]) + 1,
        field: match[2],
    };
};

/** Batch endpoints report failures per spreadsheet row as `{ RowIndex, Reason[] }`. */
const batchRowReason = (
    error: any,
    rowFallback?: BatchRowFallback,
): string | null => {
    const rowIndex = error?.RowIndex ?? error?.rowIndex;
    const reason = error?.Reason ?? error?.reason;

    if (Array.isArray(reason) && reason.length) {
        const reasons = reason.join(' ');
        return Number.isInteger(rowIndex) ? `#${rowIndex}: ${reasons}` : reasons;
    }

    if (typeof reason === 'string' && reason.trim()) {
        return Number.isInteger(rowIndex) ? `#${rowIndex}: ${reason}` : reason;
    }

    return Number.isInteger(rowIndex) ? rowFallback?.(rowIndex) ?? null : null;
};

export class InterceptedError extends Error {
    constructor(message: string, public readonly innerException: Error) {
        super(message);
    }

    public humanizeError(
        sanitizer: SanitizerWrapperService,
        batchRowFallback?: BatchRowFallback,
        batchFieldFallback?: BatchFieldFallback,
    ): string {
        if (this.innerException instanceof HttpErrorResponse) {
            let error = this.innerException.error;
            if (!!error && typeof(error) == 'object' && 'errors' in error) {
                error = Object.entries(error.errors).flatMap(([key, values]) => {
                    const batchField = batchValidationField(key);
                    if (batchField && batchFieldFallback) {
                        return [batchFieldFallback(batchField.rowIndex, batchField.field)];
                    }

                    return (values as any[])?.map(value => `${key}: ${value}`);
                });
            }
            if (!!error && typeof(error) == 'object' && Array.isArray(error))
                return `<ul>` + error.map(x => {
                    const message = typeof(x) == 'string'
                        ? x
                        : x?.errorMessage ?? x?.ErrorMessage ?? x?.message ?? x?.Message ?? batchRowReason(x, batchRowFallback) ?? JSON.stringify(x);
                    return `<li>${sanitizer.sanitize(message)}</li>`;
                }).join('\n') + `</ul>`;
            else {
                if (this.innerException.status < 500)
                    return typeof(error) == 'string'
                        ? error
                        : error?.errorMessage ?? error?.ErrorMessage ?? error?.message ?? error?.Message ?? this.innerException.message;
                else
                    return this.innerException.statusText;
            }
        } else {
            return this.message ?? this.innerException.message;
        }
    }
}
